import { FormattedMessage, useIntl } from 'react-intl'
import { Box } from '@mui/material'
import { Alert, Link } from '@carto/meridian-ds/components'
import { ProviderType } from 'features/connections'
import { useCurrentUser } from 'features/users/hooks/userQueryHooks'
import { isAdmin, isSuperadmin } from 'features/users/model/user'
import { AnalyticsToolBoxPaths } from 'features/account/ui/AccountAnalytics/paths'
import {
  INSTALLATION_DOCS_SNOWFLAKE,
  INSTALLATION_DOCS_BIGQUERY,
  INSTALLATION_DOCS_REDSHIFT,
  INSTALLATION_DOCS_POSTGRES,
  INSTALLATION_DOCS_DATABRICKS
} from 'app/constants/urls'

interface AnalyticsToolboxBannerProps {
  provider: ProviderType
  onClose: () => void
}

const InstallDocsLinks = {
  [ProviderType.BIGQUERY]: INSTALLATION_DOCS_BIGQUERY,
  [ProviderType.SNOWFLAKE]: INSTALLATION_DOCS_SNOWFLAKE,
  [ProviderType.DATABRICKS]: INSTALLATION_DOCS_DATABRICKS,
  [ProviderType.DATABRICKS_REST]: INSTALLATION_DOCS_DATABRICKS,
  [ProviderType.DATABRICKS_WAREHOUSE]: INSTALLATION_DOCS_DATABRICKS,
  [ProviderType.REDSHIFT]: INSTALLATION_DOCS_REDSHIFT,
  [ProviderType.POSTGRES]: INSTALLATION_DOCS_POSTGRES
}

const atAutomaticInstallationSupportedProviders = [ProviderType.SNOWFLAKE]

function AnalyticsToolboxBanner({ provider, onClose }: AnalyticsToolboxBannerProps) {
  const intl = useIntl()
  const providerCapitalized = intl.formatMessage({ id: `data-explorer.providers.${provider}` })
  const toolboxDocsLink = `https://docs.carto.com/analytics-toolbox-${provider}/overview/getting-started/`

  const atAutomaticInstallationSupported = atAutomaticInstallationSupportedProviders.includes(provider)
  const user = useCurrentUser({ enabled: atAutomaticInstallationSupported })
  const isAdminUser = user && isAdmin(user)
  const isSuperadminUser = user && isSuperadmin(user)
  const atAutomaticInstallationEnabled = (isAdminUser || isSuperadminUser) && atAutomaticInstallationSupported

  return (
    <Box mt={2}>
      <Alert
        severity="info"
        content="block"
        onClose={onClose}
        title={
          <FormattedMessage
            id="connections.create.analyticsToolboxBanner.title"
            values={{ provider: providerCapitalized }}
          />
        }
      >
        <FormattedMessage
          id={`connections.create.analyticsToolboxBanner.content.${provider}`}
          values={{
            provider: providerCapitalized,
            analyticsToolboxLink: (
              <Link href={toolboxDocsLink} target="_blank" rel="noopener noreferrer">
                <FormattedMessage id="connections.create.analyticsToolboxBanner.analyticsToolboxLink" />
              </Link>
            ),
            guideLink: (
              <Link href={InstallDocsLinks[provider]} target="_blank" rel="noopener noreferrer">
                <FormattedMessage
                  id={`connections.create.analyticsToolboxBanner.${
                    provider === ProviderType.POSTGRES || provider === ProviderType.BIGQUERY
                      ? 'documentationLink'
                      : 'guideLink'
                  }`}
                />
              </Link>
            )
          }}
        />

        {atAutomaticInstallationSupported && (
          <Box mt={1}>
            <FormattedMessage
              id={`connections.create.analyticsToolboxBanner.installAvailable.${
                atAutomaticInstallationEnabled ? 'enabled' : 'disabled'
              }`}
              values={{
                provider: providerCapitalized,
                a: (content) => (
                  <Link target="_blank" rel="noreferrer" href={AnalyticsToolBoxPaths.ConnectWarehouse(provider)}>
                    {content}
                  </Link>
                )
              }}
            />
          </Box>
        )}

        {provider === ProviderType.REDSHIFT && (
          <Box mt={1}>
            <FormattedMessage
              id={`connections.create.analyticsToolboxBanner.subContent.${provider}`}
              values={{
                provider: providerCapitalized
              }}
            />
          </Box>
        )}
      </Alert>
    </Box>
  )
}

export default AnalyticsToolboxBanner
